import {Row} from "@tanstack/react-table";
import {mkConfig, generateCsv, download} from "export-to-csv";

const csvConfig = mkConfig({
  fieldSeparator: ",",
  filename: "Convention Orders",
  decimalSeparator: ".",
  useKeysAsHeaders: true,
});

export const exportToCSV = (rows: Row<any>[]) => {
  const rowData = rows.map((row) => flattenForCsv(row.original));
  const csv = generateCsv(csvConfig)(rowData);
  download(csvConfig)(csv);
};

function flattenForCsv(
  input: any
): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  if (input == null) return out;

  const walk = (value: any, prefix: string) => {
    if (value == null) {
      out[prefix] = "";
      return;
    }

    // Special-case: order_answers array → use question text as headers
    if (Array.isArray(value) && looksLikeOrderAnswers(value)) {
      flattenOrderAnswers(value, out);
      return;
    }

    if (Array.isArray(value)) {
      // If primitives, join; else index
      if (
        value.every(
          (v) => v == null || ["string", "number", "boolean"].includes(typeof v)
        )
      ) {
        out[prefix] = value.map((v) => (v == null ? "" : String(v))).join("; ");
      } else {
        value.forEach((v, i) => walk(v, `${prefix}[${i}]`));
      }
      return;
    }

    if (typeof value === "object") {
      Object.entries(value).forEach(([k, v]) => {
        const key = prefix ? `${prefix}.${k}` : k;
        walk(v, key);
      });
      return;
    }

    out[prefix] = toPrimitive(value);
  };

  if (typeof input === "object" && !Array.isArray(input)) {
    Object.entries(input).forEach(([k, v]) => walk(v, k));
  } else {
    walk(input, "value");
  }

  return out;
}

function looksLikeOrderAnswers(arr: any[]): boolean {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every(
    (item) =>
      item &&
      typeof item === "object" &&
      "answer" in item &&
      item.eventQuestion &&
      typeof item.eventQuestion === "object" &&
      "question" in item.eventQuestion
  );
}

function flattenOrderAnswers(
  answers: Array<{answer: any; eventQuestion: {question: string}}>,
  out: Record<string, string | number | boolean | null>
) {
  const seen = new Map<string, number>();
  answers.forEach((a) => {
    const base = sanitizeHeader(a.eventQuestion?.question ?? "Question");
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    const header = count === 0 ? base : `${base} (${count + 1})`;
    out[header] = toPrimitive(a.answer);
  });
}

function sanitizeHeader(s: string): string {
  return String(s)
    .replace(/[\r\n]/g, " ")
    .replace(/"/g, "'")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toPrimitive(value: any): string | number | boolean | null {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
